import { productionParameters } from '§§G/production-parameters'

import type { ProductionParameterName, ProductionParameterOwnerTable } from '§§G/production-parameters'
import type { ProductionParametersOf } from '§M/types/productionparams.type'

/**
 * The parameter names an owner table carries, in definition order
 */
export function productionParameterNamesFor<Owner extends ProductionParameterOwnerTable>(
  owner: Owner
): Array<keyof ProductionParametersOf<Owner> & ProductionParameterName> {
  return (Object.keys(productionParameters) as ProductionParameterName[]).filter(name =>
    productionParameters[name].owners.some(entry => entry.table === owner)
  ) as Array<keyof ProductionParametersOf<Owner> & ProductionParameterName>
}

/**
 * Pick the owner's production parameters off an object that carries them
 * under the bare parameter names (an entity, a DTO, a response item).
 * Absent properties are left out, so the result can seed a create payload
 * without overriding the domain defaults.
 */
export function pickProductionParameters<Owner extends ProductionParameterOwnerTable>(
  source: object,
  owner: Owner
): Partial<ProductionParametersOf<Owner>> {
  const picked: Record<string, unknown> = {}
  const record = source as Record<string, unknown>
  for (const name of productionParameterNamesFor(owner)) {
    if (record[name] !== undefined) picked[name] = record[name]
  }
  return picked as Partial<ProductionParametersOf<Owner>>
}

/**
 * Copy the parameters one owner shares with another: a project's setup
 * template onto a new concept (the makeup water block), a setup department
 * onto a section department (all of them), a section department onto a cycle
 * node (the five it overrides). Only parameters present on the source are
 * copied.
 */
export function copyProductionParameters<Target extends ProductionParameterOwnerTable>(
  source: object,
  target: Target
): Partial<ProductionParametersOf<Target>> {
  return pickProductionParameters(source, target)
}

/**
 * The domain defaults of an owner's parameters (lookup refs default to null)
 */
export function defaultProductionParametersFor<Owner extends ProductionParameterOwnerTable>(
  owner: Owner
): ProductionParametersOf<Owner> {
  const defaults: Record<string, unknown> = {}
  for (const name of productionParameterNamesFor(owner)) {
    defaults[name] = productionParameters[name].default
  }
  return defaults as ProductionParametersOf<Owner>
}
